import {Container, Grid} from "@mui/material";
import React, {useEffect, useState} from "react";
import PageGridPlacement from "../layouts/body/PageGridPlacement";
import {
    CmLinkable,
    PageGridPlacement as CmPageGridPlacement
} from "../../../packages/graphql-layer/src";
import Navigation from "../layouts/navigation/Navigation";
import Breadcrumb from "../layouts/navigation/Breadcrumb";
import TopNavi from "../layouts/topnavi/TopNavi";
import CCORichText from "../fundamentals/CCORichText/CCORichText";
import CCOContainer from "../fundamentals/CCOContainer";
import CCOComponentWrapper from "../fundamentals/CCOComponentWrapper";
import CCOCartFab from "../fundamentals/CCOCartFab";
import {useCartContext} from "../../context/CartContext";
import {useRouter} from "next/router";
import routes from "../../common/constants/routes";
import {getRouteUrl} from "../../utility/navigationUtil";
import {usePageContext} from "../../context/PageContext";
import CCOH1 from "../fundamentals/CCOH1";
import {
    getPageContentType,
    isCart,
    isOfferPage, isProductPage,
    isSpecialTopicPage,
    PageContentType,
    placement_footer,
    placement_header,
    placement_stageteaser,
    placement_topnavi,
    queryList,
    sosEvent,
    sosNews,
    sosPressrelease,
    sosPublication,
    viewTypeSpecialTopic
} from "./pageLayoutsUtils";
import useNavigationHeight from "../../utility/useNavigationHeight";
import PreviewElementBoxDrawer from "../preview/PreviewElementBoxDrawer";
import useWindowSize from "../../utility/useWindowSize";
import dynamic from "next/dynamic";
import P020DonationProduct from "../doctypes/P020DonationProduct/P020DonationProduct";

interface Props {
    isHideNavi?: boolean,
    navigation: any,
    page: any,
    content?: any,
    CAEGlobalSettings: any,
    children?: JSX.Element,
    currentURL: string,
    csrfToken: string,
    pageSettings?: any,
    credentials?: any
}

const P013News = dynamic(() => import("../doctypes/P013News"));

const P014Event = dynamic(() => import("../doctypes/P014Event"));

const P012Pressrelease = dynamic(() => import("../doctypes/P012Pressrelease"));

const LogoOnlyNavigation = dynamic(() => import("../layouts/navigation/LogoOnlyNavigation"));

const P011Publication = dynamic(() => import("../doctypes/P011Publication/P011Publication"));

const P015Cart = dynamic(() => import("../doctypes/cart/P015Cart"));

const Footer = dynamic(() => import("../layouts/footer/Footer"));

export function checkForSpecialTopics(page: any) {
    let hasSpecialTopicPlaceholder = false;
    page.grid.placements.map((placement: CmPageGridPlacement) => {
        placement.items.map((item: CmLinkable) => {
            if (item.viewtype === viewTypeSpecialTopic || item.viewtype === "c021_kachel_teaser_with_special_topics") {
                hasSpecialTopicPlaceholder = true;
                return true;
            }
        })
    });
    return hasSpecialTopicPlaceholder;
}

export function checkForQueryList(page: any) {
    return page.grid.placements.filter((placement: CmPageGridPlacement) => {
        return placement.items.filter((item: CmLinkable) => item.type === queryList).length > 0
    }).length > 0
}

function HomepageLayout(props: Props) {
    const [shouldRenderCartFab, setShouldRenderCartFab] = useState(false);
    const pageContext = usePageContext();
    const cartLink = getRouteUrl(pageContext.rootUrl, routes.PUBLICATIONS.CART)
    const {isLoadingBreakpoints, greaterThanWindowLarge} = useWindowSize();
    const navHeight = useNavigationHeight(props.isHideNavi || pageContext.hideTopNavi);
    const {isCartFilled} = useCartContext();
    const {asPath} = useRouter();
    const header = props.page?.grid.placements.find((p: CmPageGridPlacement) => p.name === placement_header);

    useEffect(() => {
        const showCart = isCartFilled && !asPath.includes(cartLink);
        setShouldRenderCartFab(showCart);
    }, [isCartFilled, asPath, cartLink]);


    // Set a dynamic scroll padding so that the scroll target is not below the header
    useEffect(() => {
        if (!document || navHeight === undefined) return;
        document.body.style.scrollPaddingTop = navHeight;
        document.documentElement.style.scrollPaddingTop = navHeight;
    }, [navHeight]);

    if (isLoadingBreakpoints) return null

    let pageContentType = getPageContentType(props.content?.content?.content?.type, props.currentURL);
    if (isProductPage(props.page)) {
        pageContentType = PageContentType.Product;
    }
    return <React.Fragment key={placement_topnavi}>
        {
            props.isHideNavi ? <LogoOnlyNavigation navigation={props.navigation} page={props.page} header={header}/> :
                <>
                    {renderTopNavi(props, greaterThanWindowLarge)}
                    <Navigation navigation={props.navigation} page={props.page} header={header}/>
                </>
        }
        <Container sx={{height: navHeight}}/>
        <Grid container
              width='100%'
              padding='0!important'
              margin='0!important'
              direction='column'
              justifyContent='center'
              alignItems='stretch'
              spacing={1}>
            <Grid id='grid' item pt='0!important' width='100%' padding='0!important' margin='0!important'>
                {
                    renderPageContent(props, pageContentType, props.isHideNavi)
                }
                {
                    renderPlacements(props, pageContentType)
                }
            </Grid>
        </Grid>
        {shouldRenderCartFab && <CCOCartFab/>}
    </React.Fragment>
}

function renderTopNavi(props: Props, breakPointLg: boolean) {
    return breakPointLg ? <TopNavi empty={props.isHideNavi}
                                   header={props.page?.grid.placements.find((p: CmPageGridPlacement) => p.name === placement_header)}/> : null
}

function renderPageContent(props: Props, pageContentType: PageContentType, isHideNavi?: boolean) {
    switch (pageContentType) {
        case PageContentType.Product:
            return <P020DonationProduct product={props.page} CAEGlobalSettings={props.CAEGlobalSettings}/>
        case PageContentType.Cart:
            return renderCartContent(props);
        case PageContentType.Standard:
            return renderStandardContent(props, isHideNavi);
        case PageContentType.Special:
            return renderSpecialContent(props);
        default:
            return null;
    }
}

export function renderStandardContent(props: Props, isHideNavi?: boolean) {
    const isOffer = props.page && isOfferPage(props.page);
    const hasContent = props.page && (props.page.teaserText || props.page.detailText || props.page.title);
    const hasSpecialTopicPlaceholder = checkForSpecialTopics(props.page);
    const pageContainsQueryList = checkForQueryList(props.page);
    const isH1Activated: boolean = !props.page.settings.deactivateH1;
    if (!(hasContent || hasSpecialTopicPlaceholder || isOffer || pageContainsQueryList)) {
        return null;
    }

    return (
        <>
            {props.page.grid.placements.map((p: CmPageGridPlacement, index: number) =>
                renderPlacement(p, index, props.CAEGlobalSettings, props.csrfToken, false, "stageteaser", props.page, props.pageSettings))}
            {props.page && !isHideNavi && <Breadcrumb target={props.page}/>}
            <CCOComponentWrapper customStyle={{marginBottom: {xl: 12, lg: 12, md: 12, sm: 6, xs: 6}}} id='main'>
                <CCOContainer dataCypress='standardPlacement' width='small'>
                    <PreviewElementBoxDrawer propertyId={props.page.id}>
                        <CCOH1 metadataId={props.page.id} propertyName='properties.title' title={props.page?.title} isH1Activated={isH1Activated}></CCOH1>
                        {renderRichText(props.page.detailText, props.page.teaserText, props.page.id, props.pageSettings?.ignoreTeaserText ?? false, isHideNavi)}
                    </PreviewElementBoxDrawer>
                </CCOContainer>
            </CCOComponentWrapper>

        </>)

}

function renderRichText (detailText: any, teaserText: any, metadataId: any, ignoreTeaserText:boolean, isHideNavi: boolean | undefined) {
    if (ignoreTeaserText) {
        return <></>;
    }
    if (detailText) {
        return <CCORichText metadataId={metadataId} textAsTree={detailText.textAsTree} isHideNavi={isHideNavi} propertyName={'properties.detailText'}/>
    }
    if (teaserText) {
        return <CCORichText metadataId={metadataId} textAsTree={teaserText.textAsTree} isHideNavi={isHideNavi} propertyName={'properties.teaserText'}/>
    }
    return <></>;
}

function renderPlacements(props: Props, pageContentType: PageContentType) {
    const isStandardContent = pageContentType === PageContentType.Standard;
    const credentials = props.credentials;
    return props.page.grid.placements.map((p: CmPageGridPlacement, index: number) => renderPlacement(p, index, props.CAEGlobalSettings, props.csrfToken, isStandardContent, undefined, props.page, props.pageSettings, props.isHideNavi, credentials));
}

function renderPlacement(placement: CmPageGridPlacement, index: number, CAEGlobalSettings: any, csrfToken: string, isStandardContent?: boolean, placementName?: string, page?: CmLinkable, pageSettings?: any, isHideNavi?: boolean, credentials?: any) {
    switch (placement.name) {
        case placement_header: {
            return null
        }
        case placement_stageteaser: {
            if (placementName && placementName === placement.name) {
                return <PageGridPlacement placementName={placement_stageteaser} key={index} items={placement.items}
                                          viewtype={placement.viewtype || ''}
                                          CAEGlobalSettings={CAEGlobalSettings} csrfToken={csrfToken}/>
            } else return null
        }
        case placement_footer: {
            if (!placementName || placementName === placement.name) {
                return <Footer key={"footer" + index} CAEGlobalSettings={CAEGlobalSettings} items={placement.items}
                               page={page} isHideNavi={isHideNavi}/>

            } else return null
        }
        default: {
            if (isStandardContent) {
                return <PageGridPlacement placementName={placement.name} key={index} items={placement.items}
                                          viewtype={placement.viewtype || ''}
                                          CAEGlobalSettings={CAEGlobalSettings} csrfToken={csrfToken}
                                          placeholderContent={page && isSpecialTopicPage(page) ? (page) : undefined}
                                          pageSettings={pageSettings} credentials={credentials}
                />

            } else return null
        }
    }
}

export function renderSpecialContent(props: Props) {
    const content = props?.content?.content?.content;
    switch (content?.type) {
        case sosEvent:
            return <P014Event sosEvent={content} csrfToken={props.csrfToken}
                              CAEGlobalSettings={props.CAEGlobalSettings} page={props.page}/>
        case sosNews:
            return <P013News sosNews={content} csrfToken={props.csrfToken}
                             CAEGlobalSettings={props.CAEGlobalSettings}/>
        case sosPressrelease:
            return <P012Pressrelease sosPressrelease={content} csrfToken={props.csrfToken}
                                     CAEGlobalSettings={props.CAEGlobalSettings}/>
        case sosPublication:
            return <P011Publication sosPublication={content} backUri={props.page.selfUrl}/>
        default:
            return null;
    }
}

function renderCartContent(props: Props) {
    return isCart(props.currentURL) ? <P015Cart page={props.page} pageSettings={props.pageSettings}/> : ""
}

export default HomepageLayout